/**
 * Painel lateral com os PAGAMENTOS ligados a um título.
 *
 * O QUE MUDOU DEPOIS DO PRIMEIRO USO (09/10/2026)
 * -----------------------------------------------
 * A primeira versão cruzava as `ligacoes` com a página carregada do razão, e
 * avisava quando algo não estava ali. O usuário leu o aviso e entendeu "é de
 * outro ano" — pedindo que trouxéssemos "TODOS os lançamentos, mesmo se for de
 * meses ou anos anteriores".
 *
 * Medido na NF 22401 (856/177): os 9 ausentes estavam TODOS em 2026, entre
 * 15/01 e 20/04. Não era janela de data — era paginação: a conta tem 4.435
 * lançamentos no ano e a página do razão traz 200.
 *
 * Por isso o painel agora busca PELO NÚMERO (`/contabil/lancamentos`), sem
 * depender de página nenhuma. A janela do período não filtra: quando o
 * lançamento está fora dela, a linha vem marcada — que é o "apenas indicar
 * isto" do pedido.
 *
 * NAVEGAÇÃO
 * ---------
 * Segundo pedido: "clicar no lançamento e ir direto para a linha dele".
 * O destino é a aba RAZÃO, e não a composição: medido, a baixa é consumida
 * pelo matching e não aparece em `tb_composicao_saldo_item` — só a provisão
 * fica. A página certa vem de `/razao/posicao`, que ordena igual ao razão.
 *
 * Toda regra de leitura vive em `ligacoes.ts` (função pura, testada).
 */

import React from 'react';
import {
  Box,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Spinner,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { ArrowSquareOut, Info } from 'phosphor-react';

import { IComposicaoItem, ILancamentoLigado } from './papelTrabalho';
import { pctPago, qtdLigacoes, restaPagar, somaLigados, tituloDoItem } from './ligacoes';

const COR = {
  ink: '#0F172A',
  muted: '#64748B',
  faint: '#94A3B8',
  linha: 'rgba(15, 23, 42, 0.08)',
  ok: '#059669',
  warn: '#D97706',
  warnBg: 'rgba(217, 119, 6, 0.08)',
  brand: '#1B88F5',
} as const;

const NUM = { fontVariantNumeric: 'tabular-nums' } as const;

const fmt = (n: number | null | undefined): string =>
  n == null || !Number.isFinite(Number(n))
    ? '—'
    : Number(n).toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

const fmtData = (d: string | null | undefined): string => {
  if (!d) return '—';
  const [a, m, dia] = d.slice(0, 10).split('-');
  return a && m && dia ? `${dia}/${m}/${a}` : d;
};

const Par: React.FC<{ rotulo: string; valor: string; cor?: string; nota?: string }> = ({
  rotulo,
  valor,
  cor,
  nota,
}) => (
  <Box flex="1 1 120px">
    <Text
      fontSize="9.5px"
      fontWeight="700"
      color={COR.faint}
      textTransform="uppercase"
      letterSpacing="0.06em"
      mb={0.5}
    >
      {rotulo}
    </Text>
    <Text fontSize="15px" fontWeight="700" color={cor ?? COR.ink} sx={NUM}>
      {valor}
    </Text>
    {nota && (
      <Text fontSize="10.5px" color={COR.muted} sx={NUM}>
        {nota}
      </Text>
    )}
  </Box>
);

interface IProps {
  item: IComposicaoItem | null;
  /** Lançamentos buscados PELO NÚMERO — não dependem da página do razão. */
  lancamentos?: ILancamentoLigado[];
  carregando?: boolean;
  aberto: boolean;
  onFechar: () => void;
  /** Leva à linha do lançamento no razão. */
  onIrAoRazao?: (l: ILancamentoLigado) => void;
}

export const DrawerLigacoes: React.FC<IProps> = ({
  item,
  lancamentos,
  carregando,
  aberto,
  onFechar,
  onIrAoRazao,
}) => {
  const ls = lancamentos ?? [];
  const esperados = item ? qtdLigacoes(item) : 0;
  const pct = item ? pctPago(item) : null;
  // Quando a busca por numero devolve menos que o esperado, o lancamento foi
  // excluido da base — e' raro, mas calar faria a soma parecer errada.
  const naoAchados = Math.max(0, esperados - ls.length);

  return (
    <Drawer isOpen={aberto} placement="right" onClose={onFechar} size="md">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth="1px" borderColor={COR.linha} pb={3}>
          <Text fontSize="14px" fontWeight="700" color={COR.ink}>
            {item ? tituloDoItem(item) : 'Pagamentos'}
          </Text>
          {item?.fornecedor && (
            <Text fontSize="11.5px" color={COR.muted} fontWeight="500" noOfLines={1}>
              {item.fornecedor}
            </Text>
          )}
        </DrawerHeader>

        <DrawerBody px={5} py={4}>
          {!item ? null : (
            <>
              <Flex gap={4} wrap="wrap" pb={4} borderBottom="1px solid" borderColor={COR.linha}>
                <Par rotulo="Valor original" valor={fmt(item.valor_original)} />
                <Par
                  rotulo="Já pago"
                  valor={fmt(item.valor_baixado)}
                  cor={Number(item.valor_baixado ?? 0) > 0 ? COR.ok : COR.faint}
                  nota={pct == null ? undefined : `${pct.toFixed(1)}% do título`}
                />
                <Par rotulo="Resta" valor={fmt(restaPagar(item))} />
              </Flex>

              <Flex align="center" gap={2} mt={4} mb={2}>
                <Text
                  fontSize="10px"
                  fontWeight="700"
                  color={COR.faint}
                  textTransform="uppercase"
                  letterSpacing="0.06em"
                >
                  {esperados} pagamento{esperados === 1 ? '' : 's'} identificado
                  {esperados === 1 ? '' : 's'}
                </Text>
                {carregando && <Spinner size="xs" color={COR.faint} />}
              </Flex>

              {ls.map(l => (
                <Box
                  key={l.id}
                  as={onIrAoRazao ? 'button' : 'div'}
                  w="100%"
                  textAlign="left"
                  py={2.5}
                  px={1}
                  borderBottom="1px solid"
                  borderColor={COR.linha}
                  cursor={onIrAoRazao ? 'pointer' : 'default'}
                  _hover={{ bg: 'rgba(27, 136, 245, 0.05)' }}
                  onClick={onIrAoRazao ? () => onIrAoRazao(l) : undefined}
                  title={onIrAoRazao ? 'Abrir este lançamento no razão' : undefined}
                >
                  <Flex justify="space-between" gap={3} align="baseline">
                    <Flex align="center" gap={1.5}>
                      <Text fontSize="12px" fontWeight="600" color={COR.ink} sx={NUM}>
                        {fmtData(l.data)}
                      </Text>
                      {l.fora_da_janela && (
                        <Tooltip
                          label="Fora do período selecionado na tela. O pagamento existe e está ligado a este título."
                          fontSize="11px"
                          placement="top"
                          hasArrow
                        >
                          <Text
                            fontSize="9.5px"
                            fontWeight="700"
                            color={COR.warn}
                            bg={COR.warnBg}
                            px={1.5}
                            borderRadius="full"
                            cursor="help"
                          >
                            outro período
                          </Text>
                        </Tooltip>
                      )}
                    </Flex>
                    <Flex align="center" gap={1.5}>
                      <Text fontSize="13px" fontWeight="700" color={COR.ink} sx={NUM}>
                        {fmt(l.valor)}
                      </Text>
                      {onIrAoRazao && <ArrowSquareOut size={12} color={COR.brand} />}
                    </Flex>
                  </Flex>
                  <Text fontSize="11px" color={COR.muted} mt={0.5} noOfLines={2}>
                    {l.historico ?? '—'}
                  </Text>
                  <Flex gap={3} mt={1} wrap="wrap">
                    <Text fontSize="10.5px" color={COR.faint} sx={NUM}>
                      Lanç. {l.numero ?? '—'}
                    </Text>
                    {l.contrapartida_label && (
                      <Text fontSize="10.5px" color={COR.faint} noOfLines={1}>
                        {l.contrapartida_label}
                      </Text>
                    )}
                  </Flex>
                </Box>
              ))}

              {ls.length > 0 && (
                <Flex justify="space-between" pt={3} gap={3}>
                  <Text fontSize="11px" fontWeight="700" color={COR.muted}>
                    Soma dos {ls.length} pagamento{ls.length === 1 ? '' : 's'}
                  </Text>
                  <Text fontSize="12px" fontWeight="700" color={COR.ink} sx={NUM}>
                    {fmt(somaLigados(ls))}
                  </Text>
                </Flex>
              )}

              {!carregando && naoAchados > 0 && (
                <Flex
                  align="flex-start"
                  gap={2}
                  bg={COR.warnBg}
                  borderRadius="8px"
                  px={3}
                  py={2}
                  mt={3}
                >
                  <Box color={COR.warn} mt="1px">
                    <Info size={13} />
                  </Box>
                  <Text fontSize="11px" color={COR.warn} lineHeight="1.5">
                    {naoAchados} pagamento{naoAchados === 1 ? '' : 's'} ligado
                    {naoAchados === 1 ? '' : 's'} a este título não{' '}
                    {naoAchados === 1 ? 'foi encontrado' : 'foram encontrados'} no razão da empresa.
                  </Text>
                </Flex>
              )}
            </>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

export default DrawerLigacoes;
